// src/context/WishlistContext.js
//
// Wishlist UI state is a mirror of the last Wishlist API response or agent
// wishlist snapshot — the same model as CartContext. Manual mutations go
// through REST /api/wishlist and mirror its reply; agent mutations apply the
// agent payload directly (no Wishlist API call). Nothing lives in
// localStorage: until 2026-10-22 the list was kept there per shopper, was
// never read back from the server and vanished on sign-out (known issue C4).
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  fetchWishlist,
  addWishlistItem,
  removeWishlistItem,
} from "../api/wishlistApi";
import {
  trackWishlistChanged,
  isAgentActionActive,
  UI_EVENT_STALE_MS,
} from "../services/uiAgentSync";
import { useAuth } from "./AuthContext";
import { getUserProfile } from "../utils/auth";
import { flyToBag, productImageOf } from "../utils/flyToBag";
import { showToast } from "../utils/toastManager";

const WishlistContext = createContext();

/**
 * Normalize an item from any source — the API's WishlistItemResponse
 * (`product_id`, `title`, `price`, `image_uri`), an agent view_wishlist row,
 * an add/remove snapshot, a product card — into the one shape the UI reads.
 */
function normalizeItem(item) {
  const rawId = item.product_id ?? item.article_id ?? item.id;
  const id = rawId != null ? String(rawId) : "";
  const rawPrice = item.discounted_price ?? item.price;
  return {
    ...item,
    id,
    article_id: id,
    product_id: id,
    name: item.enhanced_prod_name ?? item.name ?? item.title ?? "",
    title: item.enhanced_prod_name ?? item.title ?? item.name ?? "",
    image: item.image_uri ?? item.image ?? item.image_url ?? "",
    price: typeof rawPrice === "number" ? rawPrice : parseFloat(String(rawPrice ?? 0)) || 0,
    currency: typeof item.currency === "string" && item.currency ? item.currency : "USD",
  };
}

/**
 * Pull the list out of a WishlistApiResponse envelope: `data.wishlist` on a
 * GET, `data.item.wishlist` on a POST / DELETE (the mutation reply carries
 * the touched item with the whole list inside it).
 */
function wishlistSummaryFromApiResponse(apiRes) {
  if (!apiRes || typeof apiRes !== "object") return null;
  const data = apiRes.data;
  if (data != null && typeof data === "object") {
    if (Array.isArray(data.wishlist?.items)) return data.wishlist;
    if (Array.isArray(data.item?.wishlist?.items)) return data.item.wishlist;
  }
  // Some gateways may return the summary at the top level
  if (Array.isArray(apiRes.items)) return apiRes;
  return null;
}

const signedInCustomerId = () => getUserProfile()?.customer_id ?? null;

export function WishlistProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [fetching, setFetching] = useState(false);

  // A ref mirror of the current items lets the stable-callback mutators check
  // membership without stale closures, so no-op add/remove never calls the
  // API or sends events.
  const wishlistItemsRef = useRef([]);
  wishlistItemsRef.current = wishlistItems;

  // --- UI → Agent sync: queue events at mutation time, flush after commit ---
  const pendingUiEventsRef = useRef([]);
  const queueWishlistUiEvent = useCallback((event) => {
    pendingUiEventsRef.current.push({
      ...event,
      source: isAgentActionActive() ? "agent" : "manual",
      at: Date.now(),
    });
  }, []);

  useEffect(() => {
    if (!pendingUiEventsRef.current.length) return;
    const pending = pendingUiEventsRef.current;
    pendingUiEventsRef.current = [];
    const now = Date.now();
    pending.forEach(({ at, ...event }) => {
      if (now - at > UI_EVENT_STALE_MS) return; // mutation never committed
      trackWishlistChanged(event);
    });
  }, [wishlistItems]);

  /** Apply a wishlist snapshot (API summary rows or agent payload rows). */
  const applyWishlistSnapshot = useCallback((items) => {
    setWishlistItems(Array.isArray(items) ? items.map(normalizeItem) : []);
  }, []);

  const applyFromApiResponse = useCallback(
    (apiRes) => {
      const summary = wishlistSummaryFromApiResponse(apiRes);
      if (!summary) return false;
      applyWishlistSnapshot(summary.items);
      return true;
    },
    [applyWishlistSnapshot]
  );

  /** GET /api/wishlist — hydrate after login / refresh. */
  const refreshWishlist = useCallback(async () => {
    if (!user?.customer_id) {
      setWishlistItems([]);
      return null;
    }
    setFetching(true);
    try {
      const apiRes = await fetchWishlist();
      if (apiRes) applyFromApiResponse(apiRes);
      return apiRes;
    } catch (error) {
      console.warn("[Wishlist] refreshWishlist failed:", error);
      return null;
    } finally {
      setFetching(false);
    }
  }, [user?.customer_id, applyFromApiResponse]);

  // Login / logout / page restore: load the wishlist from the API when
  // authenticated. (Sign-out is a full page load, so there is nothing to
  // unwind here beyond the empty list.)
  useEffect(() => {
    if (user?.customer_id) {
      refreshWishlist();
    } else {
      setWishlistItems([]);
    }
  }, [user?.customer_id, refreshWishlist]);

  // Also react to explicit login events, as the cart does.
  useEffect(() => {
    const onAuth = () => {
      if (signedInCustomerId()) {
        refreshWishlist();
      } else {
        setWishlistItems([]);
      }
    };
    window.addEventListener("authStateChanged", onAuth);
    return () => window.removeEventListener("authStateChanged", onAuth);
  }, [refreshWishlist]);

  /**
   * Manual add → POST /api/wishlist → mirror the reply's list.
   *
   * A product already on the list is a no-op: no call, no feedback. The
   * ghost leaves before the first await, from the card the shopper pressed
   * (a re-render may remove it — the cart has the same rule); the toast
   * waits for the server, and a failure says so instead. Resolves null
   * rather than throwing on failure: no caller handles a rejection, and
   * the toast is the report.
   */
  const addToWishlist = useCallback(
    async (product) => {
      const rawId = product?.article_id ?? product?.id;
      const articleId = rawId != null && rawId !== "" ? String(rawId) : null;
      if (!articleId) return null;
      if (wishlistItemsRef.current.some((i) => i.id === articleId)) return null;
      // Saving is an account feature; WishlistToggle asks a guest to sign in
      // first, so nothing reaches here signed out except by mistake.
      if (!signedInCustomerId()) return null;

      const item = normalizeItem({ ...product, id: articleId, article_id: articleId });

      queueWishlistUiEvent({
        action: "add",
        productId: articleId,
        productTitle: item.title || item.name || null,
      });
      flyToBag("wishlist", {
        from: isAgentActionActive() ? "agent" : "pointer",
        image: productImageOf(item),
        key: articleId,
      });

      try {
        const apiRes = await addWishlistItem({ article_id: articleId });
        if (!apiRes || !applyFromApiResponse(apiRes)) await refreshWishlist();
        showToast({
          id: "wishlist-add",
          title: "Added to wishlist",
          products: [item],
        });
        return apiRes;
      } catch (error) {
        console.warn("[Wishlist] addToWishlist API failed:", error);
        // The ghost already flew; say plainly that the save did not stick.
        showToast({
          id: "wishlist-add-error",
          title: "Couldn't save to wishlist",
          message: "Please try again.",
          severity: "error",
        });
        await refreshWishlist();
        return null;
      }
    },
    [queueWishlistUiEvent, applyFromApiResponse, refreshWishlist]
  );

  /**
   * Manual remove → DELETE /api/wishlist → mirror the reply's list.
   * No toast on success: "Move to bag" removes right after `addToCart`,
   * whose toast is the news; a failure is reported.
   */
  const removeFromWishlist = useCallback(
    async (id) => {
      const sid = String(id);
      const existing = wishlistItemsRef.current.find((item) => item.id === sid);
      if (!existing) return null;
      if (!signedInCustomerId()) return null;

      queueWishlistUiEvent({
        action: "remove",
        productId: sid,
        productTitle: existing.title || existing.name || null,
      });

      try {
        const apiRes = await removeWishlistItem({ article_id: sid });
        if (!apiRes || !applyFromApiResponse(apiRes)) await refreshWishlist();
        return apiRes;
      } catch (error) {
        console.warn("[Wishlist] removeFromWishlist API failed:", error);
        showToast({
          id: "wishlist-remove-error",
          title: "Couldn't remove from wishlist",
          message: "Please try again.",
          severity: "error",
        });
        await refreshWishlist();
        return null;
      }
    },
    [queueWishlistUiEvent, applyFromApiResponse, refreshWishlist]
  );

  const isInWishlist = (id) => {
    const sid = String(id);
    return wishlistItems.some((item) => item.id === sid || item.article_id === sid);
  };

  // --- Agent path: state only. The agent has already changed the wishlist
  // server-side; calling the API again would be a second write, and these
  // emit no ui_event (the backend would only drop its own echo). Same rule
  // as CartContext.replaceCartItemsFromAgent. ---

  /** Replace the whole list from an agent snapshot. */
  const replaceWishlistFromAgent = useCallback(
    (items) => {
      if (!Array.isArray(items)) return;
      applyWishlistSnapshot(items);
    },
    [applyWishlistSnapshot]
  );

  /** Add one item if absent. */
  const mergeItemIntoWishlist = useCallback((product) => {
    const item = normalizeItem(product);
    if (!item.id) return;
    setWishlistItems((prev) =>
      prev.some((i) => i.id === item.id) ? prev : [...prev, item]
    );
  }, []);

  /** Drop one item the agent removed. */
  const removeWishlistItemFromAgent = useCallback((id) => {
    const sid = String(id);
    setWishlistItems((prev) => prev.filter((item) => item.id !== sid));
  }, []);

  // Before AuthContext has read the cookies, a signed-in shopper would see
  // an empty list for a frame; that resolve counts as loading too.
  const wishlistLoading =
    fetching || (authLoading && Boolean(signedInCustomerId()));

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        wishlistLoading,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        replaceWishlistFromAgent,
        mergeItemIntoWishlist,
        removeWishlistItemFromAgent,
        refreshWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return context;
}
